import { Download, Tag as TagIcon, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get, type Asset, type AssetDays, type Me, type Member, type Trip } from "../../lib/api.ts";
import { dayQuery, dayTabLabel, latestDay } from "../../lib/days.ts";
import { downloadOriginals } from "../../lib/download.ts";
import type { PaletteColor } from "@trip/domain/palette";
import { useRefreshable } from "../../lib/refresh.ts";
import { useApi } from "../../lib/useApi.ts";
import { useThumbs } from "../../lib/thumbs.ts";
import { Avatar, EmptyState, ICON, PageState, reason, useToast } from "../../ui/index.ts";
import { tabKeys, tabStop } from "../../ui/tabs.ts";
import { MediaGrid } from "./MediaGrid.tsx";
import { MediaPopup } from "./MediaPopup.tsx";
import { TagPanel } from "./TagPanel.tsx";

/**
 * The trip's footage, a day at a time.
 *
 * Day tabs rather than a date range: one day is what a traveller means by "the photos from
 * Tuesday", and it bounds the page so a whole day's tiles can be held at once and filtered in the
 * browser. The person bubbles are that filter — several people can be on at once, which the
 * single-valued contributor query cannot express.
 */

/** The viewer's own zone decides where a day starts; the server counts the tabs the same way. */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const UNDATED = "undated";

/** Which tab of the strip is selected — the days in order, then "No time" — or -1 if none. */
const selectedTab = (items: { day: string }[], current: string | null) =>
  current === UNDATED ? items.length : items.findIndex((t) => t.day === current);

export function MediaTab({
  trip,
  me,
  day,
  onPicking,
  onOpen,
}: {
  trip: Trip;
  me: Me;
  /**
   * The day to show, when the host owns the clock. Given one, the grid draws no day tabs of its
   * own and reads the day it is told — the editor's map has a strip of days above both its
   * panels, and the same question asked twice on one screen gets two answers sooner or later.
   * Left out, the grid is its own master: it reads the trip's days and lands on the newest.
   */
  day?: string | null;
  /** Told when select mode opens and closes, so the shell can stand its tabs down. */
  onPicking?: (on: boolean) => void;
  /**
   * Where a tapped recording is read, when the host has somewhere of its own to read it. The
   * editor's map does: a drawer over this grid, beside a map and a clock it must leave working,
   * which is the one thing a pager that takes the whole screen cannot do.
   */
  onOpen?: (asset: Asset) => void;
}) {
  /** Whether the day is the host's to choose. `null` is a day it has yet to settle on. */
  const controlled = day !== undefined;
  // Not read at all when the host owns the day: the index is what the tabs are drawn from, and
  // there are no tabs to draw.
  const days = useApi<AssetDays>(
    controlled ? null : `/v1/trips/${trip.id}/asset-days?tz=${encodeURIComponent(TIME_ZONE)}`,
  );
  const [ownDay, setOwnDay] = useState<string | null>(null);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // The recordings themselves, not their ids: a selection outlives the day tab it was made
  // on, and the day it came from is no longer loaded when Tag or Download finally act.
  const [picked, setPicked] = useState<Map<string, Asset>>(new Map());
  // Picking is a mode the grid is in, held apart from the selection itself so the screen can
  // dress for it: the library's tabs stand down, the tiles take their toggles. It opens on the
  // first pick and closes on Cancel, or on its own once nothing is picked any more.
  const [picking, setPicking] = useState(false);
  /** Whether a day is on its way in, while the one before it is still on screen. */
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState<Asset | null>(null);
  /**
   * What the open recording is being read among: the day it was opened from, extended day by day
   * as the pager reaches either end. A day is how the grid pages its reads, not what a traveller
   * means by "the next one", so the pager's collection outgrows it.
   */
  const [strip, setStrip] = useState<Asset[]>([]);
  /** Which day each of those came from, so the strip behind can follow the card being read. */
  const dayOf = useRef(new Map<string, string>());
  /** The days already pulled into the strip, and whether one is on its way. */
  const pulled = useRef(new Set<string>());
  const pulling = useRef(false);
  const [tagging, setTagging] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);

  // Land on the latest day that holds something, once the index arrives.
  const tabs = days.data?.items ?? [];
  const landing = latestDay(tabs) ?? (days.data && days.data.undated > 0 ? UNDATED : null);
  const current = controlled ? day : (ownDay ?? landing);

  /** One day, read to the end: the bubbles filter what is already here and the grid never asks
      again as you scroll. Handed back rather than stored, because stepping out of the far end of
      a day needs the next day's recordings before it can decide which one to open. */
  const fetchDay = useCallback(
    async (which: string | null) => {
      const page: Asset[] = [];
      let cursor: string | undefined;
      do {
        const q = dayQuery(which === UNDATED ? null : which, TIME_ZONE, cursor);
        const r = await get<{ items: Asset[]; nextCursor: string | null }>(
          `/v1/trips/${trip.id}/assets?${q}`,
        );
        page.push(...r.items);
        cursor = r.nextCursor ?? undefined;
      } while (cursor);
      return page;
    },
    [trip.id],
  );

  const load = useCallback(
    async (which: string | null) => {
      setError(null);
      const page = await fetchDay(which);
      if (which !== null) for (const a of page) dayOf.current.set(a.id, which);
      setAssets(page);
    },
    [fetchDay],
  );

  // The tiles are not read through useApi, so this loader joins the pull on its own.
  useRefreshable(async () => {
    if (current !== null) await load(current);
  });

  useEffect(() => {
    if (current === null) {
      // Nothing to read: either the index says the trip has no dated footage, or the host has
      // not settled on a day. Both are an empty grid rather than a grid still loading.
      if (controlled || days.data) setAssets([]);
      return;
    }
    /*
     * The day is read without taking the last one off the screen first.
     *
     * Blanking the grid for the length of a request is what every other loader in the app
     * refuses to do — stale stays up until there is something truer to put there — and here it
     * cost more than a blink: the grid is half the workspace, and emptying it moved everything
     * beside it. `aria-busy` says what the eye cannot: this is last day's footage, briefly.
     */
    let live = true;
    setReloading(true);
    load(current)
      .catch((e: Error) => {
        if (live) setError(reason(e));
      })
      .finally(() => {
        if (live) setReloading(false);
      });
    return () => {
      live = false;
    };
  }, [current, load, days.data]);

  useEffect(() => {
    if (!current) return;
    const t = setInterval(() => void load(current).catch(() => undefined), 15_000);
    return () => clearInterval(t);
  }, [current, load]);

  /** Everyone with something in this day, you first. */
  const people = useMemo(() => {
    const out = new Map<
      string,
      { id: string; name: string; own: boolean; color: PaletteColor | null }
    >();
    for (const a of assets ?? []) {
      if (!out.has(a.contributorId)) {
        out.set(a.contributorId, {
          id: a.contributorId,
          name: a.own ? me.user.displayName : (a.contributorName ?? "A traveller"),
          own: a.own,
          // The same colour their route is drawn in, so the filter and the map agree.
          color: members.data?.items.find((m) => m.userId === a.contributorId)?.color ?? null,
        });
      }
    }
    return [...out.values()].sort((x, y) => Number(y.own) - Number(x.own));
  }, [assets, me.user.displayName, members.data]);

  /** The tabs in the order the strip draws them, with `No time` at the end, which is the order
      the library means by "the recording after this one". */
  const dayKeys = useMemo(
    () => [
      ...[...tabs].sort((a, b) => a.day.localeCompare(b.day)).map((t) => t.day),
      ...(days.data && days.data.undated > 0 ? [UNDATED] : []),
    ],
    [tabs, days.data],
  );

  const shown = useMemo(
    () => (assets ?? []).filter((a) => !hidden.has(a.contributorId)),
    [assets, hidden],
  );
  const thumbs = useThumbs(
    trip.id,
    useMemo(() => shown.filter((a) => a.thumbReady).map((a) => a.id), [shown]),
  );

  const togglePerson = (id: string) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setHidden(next);
    // Anything hidden leaves the selection with it, wherever it was picked: each recording
    // carries who shot it, so this reaches the days that are no longer loaded too.
    const kept = new Map([...picked].filter(([, a]) => !next.has(a.contributorId)));
    if (kept.size === 0 && picking) leave();
    else setPicked(kept);
  };

  const toggle = (assetId: string) => {
    const next = new Map(picked);
    if (next.has(assetId)) next.delete(assetId);
    else {
      const a = (assets ?? []).find((x) => x.id === assetId);
      if (!a) return;
      next.set(assetId, a);
    }
    // Nothing picked is nothing to do, so the mode goes with the last tile rather than
    // sitting there with a count of zero and two dead buttons.
    if (next.size === 0) leave();
    else {
      enter();
      setPicked(next);
    }
  };

  function enter() {
    if (picking) return;
    setPicking(true);
    onPicking?.(true);
  }

  function leave() {
    setPicking(false);
    setPicked(new Map());
    onPicking?.(false);
  }

  /** What is picked, and of that, what has an original safely stored to save. */
  const chosen = [...picked.values()];
  const pickedIds = new Set(picked.keys());
  const savable = chosen.flatMap((a) => {
    const key = a.components.find((c) => c.availability === "VERIFIED")?.key;
    return key ? [{ assetId: a.id, componentKey: key }] : [];
  });

  /**
   * Saves the verified originals of the selection. A recording that is not verified yet is
   * reported, never skipped silently, so nobody assumes a batch is complete when it is not.
   */
  async function downloadSelected() {
    const waiting = chosen.length - savable.length;
    setBusy(true);
    const r = await downloadOriginals(trip.id, savable);
    setBusy(false);
    toast(
      `Started ${r.saved.length} download${r.saved.length === 1 ? "" : "s"}.` +
        (waiting > 0 ? ` ${waiting} not verified yet, so left out.` : "") +
        (r.failed.length > 0 ? ` ${r.failed.length} failed.` : ""),
    );
  }

  /**
   * Extend the collection the pager is reading through, one day at a time.
   *
   * Reaching the end of a day is not the end of anything a traveller recognises, so the next day
   * that holds something is read and its recordings are added to that end of the strip — anyone
   * switched off stays switched off, and a day that is entirely theirs is skipped rather than
   * leaving a card nobody can see. One read at a time: the pager asks on every edge it touches.
   */
  async function extend(towards: -1 | 1) {
    if (pulling.current || strip.length === 0) return;
    const edge = towards < 0 ? strip[0] : strip[strip.length - 1];
    const from = edge ? dayOf.current.get(edge.id) : undefined;
    let j = dayKeys.indexOf(from ?? current ?? "") + towards;
    pulling.current = true;
    try {
      for (; j >= 0 && j < dayKeys.length; j += towards) {
        const key = dayKeys[j]!;
        if (pulled.current.has(key)) continue;
        pulled.current.add(key);
        const page = await fetchDay(key).catch(() => null);
        if (page === null) return;
        for (const a of page) dayOf.current.set(a.id, key);
        const visible = page.filter((a) => !hidden.has(a.contributorId));
        if (visible.length === 0) continue;
        setStrip((s) => (towards < 0 ? [...visible, ...s] : [...s, ...visible]));
        return;
      }
    } finally {
      pulling.current = false;
    }
  }

  /** Open a recording, reading it among the day it was opened from. */
  function openAt(asset: Asset) {
    if (onOpen) return onOpen(asset);
    pulled.current = new Set(current === null ? [] : [current]);
    dayOf.current = new Map(shown.map((a) => [a.id, current ?? UNDATED]));
    setStrip(shown);
    setOpen(asset);
  }

  const pick = (which: string | null) => {
    setOwnDay(which);
    setHidden(new Set());
  };

  return (
    <div className="stack media-tab" aria-busy={reloading}>
      {/* The way out sits at the top left, where the tabs it replaced began, and the count
          sits beside it: the buttons at the foot say what they will do, this says what is
          held. */}
      {picking && (
        <div className="select-head">
          <button type="button" className="select-x" onClick={leave}>
            <X {...ICON} aria-hidden="true" />
            Cancel
          </button>
          <strong>{picked.size} selected</strong>
        </div>
      )}

      {/* Drawn only when the day is the grid's own to pick. */}
      {!controlled && (
        <PageState {...days} isEmpty={() => false}>
          {(d) => (
            <div className="day-tabs" role="tablist" aria-label="Days" onKeyDown={tabKeys}>
              {d.items.map((t, i) => (
                <button
                  key={t.day}
                  type="button"
                  role="tab"
                  className={`day-tab${current === t.day ? " on" : ""}`}
                  aria-selected={current === t.day}
                  tabIndex={tabStop(i, selectedTab(d.items, current))}
                  onClick={() => pick(t.day)}
                >
                  <span className="day-tab-label">{dayTabLabel(t.day)}</span>
                  <span className="day-tab-count">{t.count}</span>
                </button>
              ))}
              {d.undated > 0 && (
                <button
                  type="button"
                  role="tab"
                  className={`day-tab${current === UNDATED ? " on" : ""}`}
                  aria-selected={current === UNDATED}
                  tabIndex={tabStop(d.items.length, selectedTab(d.items, current))}
                  onClick={() => pick(UNDATED)}
                >
                  <span className="day-tab-label">No time</span>
                  <span className="day-tab-count">{d.undated}</span>
                </button>
              )}
            </div>
          )}
        </PageState>
      )}

      {people.length > 1 && (
        <div className="bubbles" aria-label="People">
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              role="switch"
              aria-checked={!hidden.has(p.id)}
              aria-label={p.own ? `${p.name} (you)` : p.name}
              className={`bubble${hidden.has(p.id) ? " off" : ""}`}
              title={p.name}
              onClick={() => togglePerson(p.id)}
            >
              <Avatar name={p.name} color={p.color} />
            </button>
          ))}
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {assets === null ? (
        <p className="muted">Loading…</p>
      ) : shown.length === 0 && people.length > 0 ? (
        <EmptyState
          title="Nobody is switched on"
          body="Switch someone back on to see their recordings from this day."
        />
      ) : assets.length === 0 ? (
        <EmptyState
          title="No footage yet"
          body="Recordings appear here once they have been uploaded and checked."
        />
      ) : (
        <MediaGrid
          assets={shown}
          thumbs={thumbs}
          selected={pickedIds}
          selecting={picking}
          onOpen={openAt}
          onToggle={toggle}
        />
      )}

      {open && strip.length > 0 && (
        <MediaPopup
          trip={trip}
          items={strip}
          startId={open.id}
          members={members.data?.items ?? []}
          onEdge={(towards) => void extend(towards)}
          onActive={(a) => {
            setOpen(a);
            // The strip behind follows the card being read, so closing leaves the reader on the
            // day they actually ended up in rather than the one they started from.
            const d = dayOf.current.get(a.id);
            if (d && d !== current) setOwnDay(d);
          }}
          onClose={() => {
            // Back to the tile it came from, by id: a pointer press does not necessarily leave
            // the tile focused, so the pager cannot simply restore whatever had focus before it.
            // The grid never went away, so the tile can be handed focus before the pager goes.
            document.getElementById(`tile-${open.id}`)?.focus();
            setOpen(null);
            setStrip([]);
          }}
          onChanged={() => void load(current)}
        />
      )}

      {picking && (
        <div className="selection-bar" role="region" aria-label="Selection">
          {/* The counts are on the buttons because they are not the same number: a recording
              still on its way up can be tagged but cannot be saved yet. */}
          <button
            type="button"
            className="button primary"
            disabled={picked.size === 0}
            onClick={() => setTagging(true)}
          >
            <TagIcon {...ICON} aria-hidden="true" />
            Tag ({picked.size})
          </button>
          <button
            type="button"
            className="button"
            disabled={busy || savable.length === 0}
            onClick={() => void downloadSelected()}
          >
            <Download {...ICON} aria-hidden="true" />
            Download ({savable.length})
          </button>
        </div>
      )}

      {tagging && (
        <TagPanel
          trip={trip}
          assets={chosen}
          onDone={() => {
            setTagging(false);
            leave();
            void load(current);
          }}
          onClose={() => {
            setTagging(false);
            // The tiles carry their own tags, so whatever the sheet changed has to be read
            // back before the grid is believable again.
            void load(current);
          }}
        />
      )}
    </div>
  );
}
